import React, { useEffect, useMemo, useState } from "react";

import {
  Gift, Ticket, Sparkles, Users, BarChart3, Package,
  RefreshCw, Plus, X, CheckCircle, Search
} from "lucide-react";

/** Types **/
interface Product {
  productId: string;
  name: string;
  sku: string;
  description?: string;
  businessId?: string;
}
interface Batch {
  _id: string;
  batchId: string;
  productId: string;
  productName: string;
  quantity: number;
  activatedCount?: number;
  createdAt: string;
  status?: string;
}
interface PrizeTier {
  id: string;
  name: string;
  winners: number;
  value: number;
  description?: string;
  quantity?: number;
}
interface LotteryCampaign {
  _id: string;
  lotteryId?: string;
  name?: string;
  title?: string;
  status: "draft" | "active" | "paused" | "completed";
  startDate?: string;
  endDate?: string;
  drawDate?: string;
  entries?: number;
  totalEntries?: number;
  uniqueParticipants?: number;
  prizeTiers?: PrizeTier[];
  prizes?: PrizeTier[];
  qrTag?: string;
  selectedBatches?: string[];
  batchDetails?: Batch[];
  batchIds?: string[];
  winnersCount?: number;
}

/** API Endpoints **/
const API_BASE = "/lottery";
const BATCH_API = "/api/batches";
const PRODUCT_API = "/products";

/** Helpers for businessId and token **/
function getToken() {
  const keys = ['authToken', 'businessToken', 'token'];
  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value && value.trim()) return value.trim();
  }
  console.warn('No authentication token found in localStorage');
  return '';
}

function getCurrentBusinessId() {
  const businessData = localStorage.getItem('businessData');
  if (businessData) {
    try {
      const parsed = JSON.parse(businessData);
      if (parsed.businessId) return parsed.businessId;
      if (parsed._id) return parsed._id;
    } catch (e) {
      console.error('Error parsing businessData from localStorage:', e);
    }
  }
  const id = localStorage.getItem('businessId');
  if (id && id.trim()) return id.trim();
  console.warn('No businessId found in localStorage');
  return '';
}

/** Main Page **/
const LotteryPage: React.FC = () => {
  // Data states
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<LotteryCampaign[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // UI states
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<string>("");

  // New campaign state (added description + termsAndConditions)
  const [newCampaign, setNewCampaign] = useState({
    title: "",
    startDate: "",
    endDate: "",
    qrTag: "",
    selectedBatches: [] as string[],
    description: "",
    termsAndConditions: "",
  });
  const [tiers, setTiers] = useState<PrizeTier[]>([
    { id: "tier-1", name: "Grand Prize", winners: 1, value: 500 },
  ]);

  /* Load campaigns and products (with businessId and token) */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const businessId = getCurrentBusinessId();
        const token = getToken();

        if (!businessId) {
          console.warn('No businessId found; campaigns not loaded.');
          if (mounted) setCampaigns([]);
          return;
        }

        // Load campaigns
        try {
          const res = await fetch(`${API_BASE}/business/${encodeURIComponent(businessId)}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (!res.ok) {
            console.error('Campaigns fetch failed:', res.status);
            if (mounted) setCampaigns([]);
          } else {
            const payload = await res.json().catch(() => null);
            const items = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
            if (mounted) setCampaigns(items);
          }
        } catch (err) {
          console.error("Failed to load campaigns:", err);
          if (mounted) setCampaigns([]);
        }

        // Load products for this business
        try {
          const res2 = await fetch(`${PRODUCT_API}?businessId=${encodeURIComponent(businessId)}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (!res2.ok) {
            console.error('Products fetch failed:', res2.status);
            if (mounted) setProducts([]);
          } else {
            const payload2 = await res2.json().catch(() => null);
            const prods = Array.isArray(payload2?.data?.products) ? payload2.data.products :
                          Array.isArray(payload2?.products) ? payload2.products :
                          Array.isArray(payload2?.data) ? payload2.data :
                          Array.isArray(payload2) ? payload2 : [];
            if (mounted) setProducts(prods);
          }
        } catch (err) {
          console.error("Failed to load products:", err);
          if (mounted) setProducts([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  /* Load batches for selected product+business with token */
  useEffect(() => {
    let mounted = true;
    (async () => {
      const businessId = getCurrentBusinessId();
      const token = getToken();

      setBatches([]);
      setNewCampaign((prev) => ({ ...prev, selectedBatches: [] }));

      if (!selectedProductId) {
        setLoadingBatches(false);
        return;
      }
      if (!businessId) {
        console.warn('No businessId - cannot load batches');
        setLoadingBatches(false);
        return;
      }

      setLoadingBatches(true);
      try {
        const res = await fetch(`${BATCH_API}?businessId=${encodeURIComponent(businessId)}&productId=${encodeURIComponent(selectedProductId)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (res.status === 401) {
          alert('Session expired or unauthorized. Please log in again.');
          if (mounted) setBatches([]);
          return;
        }
        if (!res.ok) throw new Error(`Failed to fetch batches: ${res.status}`);

        const payload = await res.json().catch(() => null);
        const arr = Array.isArray(payload?.data?.batches) ? payload.data.batches :
                    Array.isArray(payload?.batches) ? payload.batches :
                    Array.isArray(payload?.data) ? payload.data :
                    Array.isArray(payload) ? payload : [];
        if (mounted) setBatches(arr);
      } catch (err) {
        console.error('Error fetching batches:', err);
        if (mounted) setBatches([]);
      } finally {
        if (mounted) setLoadingBatches(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedProductId]);

  /* Filtering */
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const s = search.toLowerCase();
      const matchSearch = !s || (c.name || c.title || "").toLowerCase().includes(s) || (c.qrTag || "").toLowerCase().includes(s);
      const matchStatus = status === "all" || c.status === status;
      return matchSearch && matchStatus;
    });
  }, [campaigns, search, status]);
  const filteredBatches = useMemo(() => {
    const term = batchSearchTerm.toLowerCase();
    return Array.isArray(batches)
      ? batches.filter((b) => 
          !term || 
          b.batchId.toLowerCase().includes(term) || 
          b.productName?.toLowerCase().includes(term)
        )
      : [];
  }, [batches, batchSearchTerm]);
  const getSelectedBatchDetails = () => Array.isArray(batches)
    ? batches.filter((b) => newCampaign.selectedBatches.includes(b._id))
    : [];

  /* Stat Totals */
  const totals = useMemo(() => {
    const entries = campaigns.reduce((a, c) => a + (c.totalEntries || 0), 0);
    const participants = campaigns.reduce((a, c) => a + (c.uniqueParticipants || 0), 0);
    const active = campaigns.filter((c) => c.status === "active").length;
    const totalPrizes = campaigns.reduce(
      (a, c) =>
        a +
        (c.prizes
          ? c.prizes.reduce(
              (s: number, t: PrizeTier) => s + (t.quantity ?? 0),
              0
            )
          : 0),
      0
    );
    return { entries, participants, active, totalPrizes };
  }, [campaigns]);

  /* Batch selection logic */
  const toggleBatch = (batchId: string) => {
    setNewCampaign((prev) => {
      const selected = prev.selectedBatches.includes(batchId)
        ? prev.selectedBatches.filter((id) => id !== batchId)
        : [...prev.selectedBatches, batchId];
      return { ...prev, selectedBatches: selected };
    });
  };
  const selectAllBatches = () => {
    setNewCampaign((prev) => ({ ...prev, selectedBatches:  filteredBatches.map((b) => b.batchId)}));
  };
  const clearAllBatches = () => {
    setNewCampaign((prev) => ({ ...prev, selectedBatches: [] }));
  };

  /* Prize tier logic */
  const addTier = () => {
    setTiers((t) => [
      ...t,
      { id: `tier-${t.length + 1}`, name: `Prize ${t.length + 1}`, winners: 1, value: 10 },
    ]);
  };
  const updateTier = (id: string, patch: Partial<PrizeTier>) => {
    setTiers((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };
  const removeTier = (id: string) => setTiers((prev) => prev.filter((t) => t.id !== id));

  /* Form validation helper (frontend) */
  const isCreateFormValid = useMemo(() => {
    if (!newCampaign.title || !newCampaign.title.trim()) return false;
    if (!newCampaign.description || !newCampaign.description.trim()) return false;
    if (!newCampaign.termsAndConditions || !newCampaign.termsAndConditions.trim()) return false;
    if (!newCampaign.startDate || !newCampaign.endDate) return false;
    const start = new Date(newCampaign.startDate);
    const end = new Date(newCampaign.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return false;
    if (!selectedProductId) return false;
    if (!newCampaign.selectedBatches || newCampaign.selectedBatches.length === 0) return false;
    return true;
  }, [newCampaign, selectedProductId]);

  /* Create campaign */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isCreateFormValid) {
      alert('Please complete all required fields: title, description, T&Cs, dates, product and batches.');
      return;
    }

    const businessId = getCurrentBusinessId();
    const token = getToken();

    if (!businessId) {
      alert('Business ID not found. Please login as a business account.');
      return;
    }
    if (!token) {
      alert('Authentication token missing. Please login and try again.');
      return;
    }

    try {
      const payload = {
        businessId,
        name: newCampaign.title,
        title: newCampaign.title,
        description: newCampaign.description,
        termsAndConditions: newCampaign.termsAndConditions,
        batchIds: newCampaign.selectedBatches,
        productIds: [selectedProductId],
        startDate: newCampaign.startDate,
        endDate: newCampaign.endDate,
        drawDate: newCampaign.endDate || newCampaign.startDate,
        prizes: tiers.map(t => ({
          rank: Number(t.id.replace("tier-", "")) || 1,
          name: t.name,
          description: t.description || "",
          value: t.value,
          quantity: t.winners,
        })),
        requireStoreActivation: true
      };

      const res = await fetch(`${API_BASE}/service/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // try to get JSON error details
        let text = '';
        try { text = await res.text(); } catch {}
        console.error('Create campaign failed', res.status, text);
        alert(`Create failed: ${res.status} ${text ? '- ' + text : ''}`);
        return;
      }

      const data = await res.json().catch(() => null);
      if (data?.success) {
        setCampaigns((prev) => [data.data, ...prev]);
        setShowCreate(false);
        setNewCampaign({
          title: "",
          startDate: "",
          endDate: "",
          qrTag: "",
          selectedBatches: [],
          description: "",
          termsAndConditions: "",
        });
        setTiers([{ id: "tier-1", name: "Grand Prize", winners: 1, value: 500 }]);
        setBatchSearchTerm("");
        setSelectedProductId("");
        alert("Lottery campaign created successfully!");
      } else {
        console.error('Unexpected create response:', data);
        alert(data?.message || "Create failed");
      }
    } catch (err) {
      console.error("Create failed:", err);
      alert("Failed to create campaign. Please try again.");
    }
  };

  /* Page render */
  if (loading)
    return (
      <div className="p-8 flex justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center text-gray-900">
            <Ticket className="w-6 h-6 text-blue-600 mr-2" /> Lottery Campaigns
          </h1>
          <p className="text-gray-600">Manage campaigns, draws, and winners.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-1" /> New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Entries" value={totals.entries} icon={<BarChart3 />} color="from-blue-500 to-indigo-600" />
        <StatCard title="Participants" value={totals.participants} icon={<Users />} color="from-green-500 to-emerald-600" />
        <StatCard title="Active" value={totals.active} icon={<Sparkles />} color="from-yellow-500 to-orange-600" />
        <StatCard title="Prizes" value={totals.totalPrizes} icon={<Gift />} color="from-purple-500 to-pink-600" />
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-2xl shadow-sm border">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search campaigns…"
                className="pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <button onClick={() => {
            setLoading(true);
            const businessId = getCurrentBusinessId();
            const token = getToken();
            fetch(`${API_BASE}/business/${businessId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            })
              .then(res => res.json())
              .then(data => setCampaigns(data.data || []))
              .finally(() => setLoading(false));
          }} className="flex items-center text-gray-600 hover:text-gray-900">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">Campaign</th>
                <th className="px-6 py-3 text-left">QR Tag</th>
                <th className="px-6 py-3 text-left">Batches</th>
                <th className="px-6 py-3 text-left">Draw Date</th>
                <th className="px-6 py-3 text-right">Entries</th>
                <th className="px-6 py-3 text-right">Winners</th>
                <th className="px-6 py-3 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredCampaigns.map((c) => (
                <tr key={c.lotteryId || c._id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium">{c.name || c.title}</td>
                  <td className="px-6 py-3">{c.qrTag || "—"}</td>
                  <td className="px-6 py-3">
                    <span className="flex items-center text-gray-600">
                      <Package className="w-4 h-4 mr-1" />
                      {(c.batchIds?.length || c.selectedBatches?.length || 0)} batches
                    </span>
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-600">
                    {c.drawDate}
                  </td>
                  <td className="px-6 py-3 text-right font-medium">{c.totalEntries || c.entries}</td>
                  <td className="px-6 py-3 text-right font-medium">{c.winnersCount || 0}</td>
                  <td className="px-6 py-3 text-right">
                    <span
                      className={`px-2 py-1 text-xs rounded-full font-medium ${
                        c.status === "active"
                          ? "bg-green-100 text-green-800"
                          : c.status === "paused"
                          ? "bg-yellow-100 text-yellow-800"
                          : c.status === "completed"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for create with batch selection */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold flex items-center">
                <Ticket className="w-5 h-5 mr-2 text-blue-600" />
                Create Lottery Campaign
              </h3>
              <button 
                onClick={() => setShowCreate(false)} 
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Campaign Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campaign Title *
                    </label>
                    <input 
                      required 
                      placeholder="e.g., Summer Grand Prize Draw" 
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500" 
                      value={newCampaign.title}
                      onChange={(e) => setNewCampaign({ ...newCampaign, title: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      QR Tag (Optional)
                    </label>
                    <input 
                      placeholder="e.g., SUMMER2025" 
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500" 
                      value={newCampaign.qrTag}
                      onChange={(e) => setNewCampaign({ ...newCampaign, qrTag: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input 
                      required 
                      type="date" 
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500" 
                      value={newCampaign.startDate}
                      onChange={(e) => setNewCampaign({ ...newCampaign, startDate: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date *
                    </label>
                    <input 
                      required 
                      type="date" 
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500" 
                      value={newCampaign.endDate}
                      onChange={(e) => setNewCampaign({ ...newCampaign, endDate: e.target.value })} 
                    />
                  </div>

                  {/* Description */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      required
                      value={newCampaign.description}
                      onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Add a short description for this campaign"
                    />
                  </div>

                  {/* Terms & Conditions */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions *</label>
                    <textarea
                      required
                      value={newCampaign.termsAndConditions}
                      onChange={(e) => setNewCampaign({ ...newCampaign, termsAndConditions: e.target.value })}
                      className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Enter terms and conditions for this campaign"
                    />
                  </div>
                </div>
              </div>

              {/* Product Selection */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Product *
                </label>
                <select
                  required
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="w-full border p-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose product...</option>
                  {products.map(product => (
                    <option key={product.productId} value={product.productId}>
                      {product.name} ({product.sku})
                    </option>
                  ))}
                </select>
              </div>

              {/* Batch Selection */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                    <Package className="w-4 h-4 mr-2 text-blue-600" />
                    Select Product Batches * ({newCampaign.selectedBatches.length} selected)
                  </h4>
                  <div className="flex space-x-2">
                    <button 
                      type="button" 
                      onClick={selectAllBatches}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      disabled={!selectedProductId || filteredBatches.length === 0}
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllBatches}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                {/* Batch Search */}
                <div className="mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search batches by ID or product name..."
                      value={batchSearchTerm}
                      onChange={(e) => setBatchSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      disabled={!selectedProductId}
                    />
                  </div>
                </div>
                {/* Selected Batches Summary */}
                {newCampaign.selectedBatches.length > 0 && (
                  <div className="mb-3 p-2 bg-white rounded-lg border">
                    <p className="text-xs font-medium text-gray-700 mb-2">Selected Batches:</p>
                    <div className="flex flex-wrap gap-2">
                      {getSelectedBatchDetails().map((batch) => (
                        <span 
                          key={batch.batchId}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                        >
                          {batch.batchId}
                          <button
                            type="button"
                            onClick={() => toggleBatch(batch.batchId)}
                            className="ml-1 hover:text-blue-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Batch List */}
                <div className="max-h-64 overflow-y-auto border rounded-lg bg-white">
                  {!selectedProductId ? (
                    <div className="p-4 text-center text-gray-500">
                      Please select a product first
                    </div>
                  ) : loadingBatches ? (
                    <div className="p-4 text-center text-gray-500">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading batches...
                    </div>
                  ) : filteredBatches.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No batches found
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredBatches.map((batch) => {
                        const isSelected = newCampaign.selectedBatches.includes(batch._id);
                        return (
                          <label
                            key={batch._id}
                            className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                              isSelected ? 'bg-blue-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              //onChange={() => toggleBatch(batch._id)}
                              onChange={() => toggleBatch(batch.batchId)}

                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{batch.batchId}</span>
                                <span className="text-xs text-gray-500">
                                  {batch.quantity} units
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">{batch.productName}</p>
                              {batch.activatedCount !== undefined && (
                                <p className="text-xs text-green-600">
                                  {batch.activatedCount} activated
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  💡 Only activations from selected batches will count as lottery entries
                </p>
              </div>
              {/* Prize Tiers */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">Prize Tiers</h4>
                  <button 
                    type="button" 
                    onClick={addTier} 
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Tier
                  </button>
                </div>
                <div className="space-y-2">
                  {tiers.map((t) => (
                    <div key={t.id} className="grid grid-cols-12 gap-2 bg-white p-3 rounded-lg border">
                      <div className="col-span-4">
                        <label className="text-xs text-gray-600">Prize Name</label>
                        <input 
                          className="w-full border p-2 rounded text-sm mt-1" 
                          value={t.name}
                          onChange={(e) => updateTier(t.id, { name: e.target.value })} 
                          placeholder="e.g., Grand Prize"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600">Winners</label>
                        <input 
                          className="w-full border p-2 rounded text-sm mt-1" 
                          type="number" 
                          min={1}
                          value={t.winners} 
                          onChange={(e) => updateTier(t.id, { winners: Number(e.target.value) })} 
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600">Value ($)</label>
                        <input 
                          className="w-full border p-2 rounded text-sm mt-1" 
                          type="number" 
                          min={0}
                          value={t.value} 
                          onChange={(e) => updateTier(t.id, { value: Number(e.target.value) })} 
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="text-xs text-gray-600">Description</label>
                        <input 
                          className="w-full border p-2 rounded text-sm mt-1" 
                          value={t.description || ""}
                          onChange={(e) => updateTier(t.id, { description: e.target.value })} 
                          placeholder="Optional"
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        <button 
                          type="button" 
                          className="w-full p-2 text-red-600 hover:bg-red-50 rounded" 
                          onClick={() => removeTier(t.id)}
                        >
                          <X className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button 
                  type="button" 
                  onClick={() => setShowCreate(false)} 
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isCreateFormValid}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* Stat Card Component */
const StatCard: React.FC<{ 
  title: string; 
  value: number; 
  icon: React.ReactNode; 
  color: string 
}> = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 flex items-center justify-between">
    <div>
      <p className="text-sm text-gray-600">{title}</p>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
    </div>
    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${color} text-white flex items-center justify-center`}>
      {icon}
    </div>
  </div>
);

export default LotteryPage;